import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import { SidebarLayout } from "@/core/shared/v2/ui/sidebar-layout";
import { Card, CatalogList, COMPACT_CARDS_THRESHOLD, FlowSteps, PageHeader, SideCart } from "../components";
import { useRoutes } from "../hooks/use-routes";
import { useUpsellPackages } from "../hooks/use-upsell-packages";
import type { BloodTestPackage as BloodTestPackageType } from "../models";
import { useCartStore } from "../store/use-cart-store";
import { useGetConfig } from "../store/use-config";
import { beginCheckoutEvent, safeTrackEvent, viewItemListEvent } from "../tracking/events";
import { checkIfAnyUpsellProductInCart } from "../utils/check-upsell-products-in-cart";

export const Upsell = () => {
  const { t } = useTranslation();
  const { products, toggleProduct } = useCartStore();
  const { upsellBloodTestPackagesBasedOnStandardProduct } = useUpsellPackages();
  const { goToSelectTime, goToPhoneVerification, goToCatalog } = useRoutes();
  const config = useGetConfig();

  const upsellPackages: BloodTestPackageType[] = useMemo(
    () => (upsellBloodTestPackagesBasedOnStandardProduct ?? []).filter((p): p is BloodTestPackageType => Boolean(p)),
    [upsellBloodTestPackagesBasedOnStandardProduct],
  );

  // Stabilize the dependency to prevent redundant view_item_list fires (matches REV-92 pattern).
  const upsellUuids = upsellPackages.map((p) => p.uuid).join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: see upsellUuids comment above
  useEffect(() => {
    if (upsellPackages.length === 0) return;
    safeTrackEvent(
      () =>
        viewItemListEvent(
          upsellPackages.map((p) => ({
            ...p,
            productType: "blood-test-package" as const,
            quantity: 1,
            item_list_id: "upsell" as const,
          })),
        ),
      "view_item_list",
      "upsell",
    );
  }, [upsellUuids]);

  const isAnyUpsellProductInCart = useMemo(
    () => checkIfAnyUpsellProductInCart(upsellBloodTestPackagesBasedOnStandardProduct, products),
    [upsellBloodTestPackagesBasedOnStandardProduct, products],
  );
  const handleOnBack = () => {
    if (config.withCalendar) {
      goToSelectTime();
    } else {
      goToCatalog();
    }
  };

  const handleNextStep = () => {
    if (products.length > 0) {
      const analyticsProducts = products.map((p) => ({
        ...p,
        productType: "blood-test-package" as const,
        quantity: 1,
        item_list_id: p.item_list_id ?? "listing",
      }));
      const totalPrice = products.reduce((acc, p) => acc + Number(p.grossPrice), 0);
      safeTrackEvent(
        () => beginCheckoutEvent(analyticsProducts, { value: totalPrice }, { numberOfProducts: products.length }),
        "begin_checkout",
        "upsell handleNextStep",
      );
    }
    goToPhoneVerification();
  };

  const cartButtonLabel = isAnyUpsellProductInCart
    ? t("GROUP_ACTIONS_ORDERING.GENERAL.GO_NEXT")
    : t("GROUP_ACTIONS_ORDERING.GENERAL.SKIP_STEP");

  return (
    <IntersectionFadeIn className="mx-auto w-full max-w-[1376px] px-4 pt-8 pb-14 md:px-8 md:pb-20 lg:px-12">
      <FlowSteps />
      <PageHeader header={`${t("cmsTranslations.upsell_page.title")}`} onBack={handleOnBack} />
      <SidebarLayout>
        <Card id="select-package-card">
          <p className="mb-6 text-base text-gray-700 lg:text-lg">{t("cmsTranslations.upsell_page.description")}</p>
          <CatalogList
            simplified={upsellPackages.length > COMPACT_CARDS_THRESHOLD}
            bloodTestPackages={upsellPackages}
            onSelect={(pkg, index) => toggleProduct(pkg, "upsell", index)}
            isProductInCart={(pkg) => products.some((product) => product.uuid === pkg.uuid)}
            buttonLabel={t("cmsTranslations.upsell_page.button")}
          />
        </Card>
        <SideCart onNextStep={handleNextStep} buttonLabel={cartButtonLabel} />
      </SidebarLayout>
    </IntersectionFadeIn>
  );
};
